import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  GlobeAltIcon,
  CubeTransparentIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  ShoppingBagIcon,
  SparklesIcon,
  ChartBarIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const EcosystemPage: React.FC = () => {
  const platforms = [
    {
      id: 1,
      name: 'DEXCHANGE',
      category: 'DeFi',
      description: 'Decentralized exchange for token swaps',
      icon: ChartBarIcon,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 5,
      name: 'LENDIFY',
      category: 'DeFi',
      description: 'Lending and borrowing protocol',
      icon: BanknotesIcon,
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 10,
      name: 'YIELDMAX',
      category: 'DeFi',
      description: 'Automated yield optimization',
      icon: SparklesIcon,
      color: 'from-yellow-500 to-orange-500'
    },
    {
      id: 15,
      name: 'NFTVERSE',
      category: 'NFT',
      description: 'NFT marketplace and gallery',
      icon: ShoppingBagIcon,
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 20,
      name: 'TEMPLUM',
      category: 'Auctions',
      description: 'Decentralized auction platform',
      icon: BuildingLibraryIcon,
      color: 'from-primary-500 to-purple-500',
      current: true
    },
    {
      id: 25,
      name: 'GOVERNANCE',
      category: 'DAO',
      description: 'Ecosystem-wide governance',
      icon: UserGroupIcon,
      color: 'from-indigo-500 to-blue-500'
    },
    {
      id: 30,
      name: 'BRIDGE',
      category: 'Infrastructure',
      description: 'Cross-chain asset bridge',
      icon: CubeTransparentIcon,
      color: 'from-cyan-500 to-teal-500'
    },
    {
      id: 35,
      name: 'INSURE',
      category: 'DeFi',
      description: 'Decentralized insurance protocol',
      icon: ShieldCheckIcon,
      color: 'from-red-500 to-orange-500'
    }
  ];

  const benefits = [
    {
      title: 'Unified Token Economy',
      description: 'NDH token powers all platforms, maximizing utility and value across the ecosystem.',
      icon: CurrencyDollarIcon
    },
    {
      title: 'Cross-Platform Rewards',
      description: 'Earn and use rewards seamlessly across all NARDIHA platforms.',
      icon: SparklesIcon
    },
    {
      title: 'Shared Liquidity',
      description: 'Deep liquidity pools shared between platforms for optimal trading.',
      icon: BanknotesIcon
    },
    {
      title: 'Ecosystem Governance',
      description: 'Single governance system for coordinated ecosystem decisions.',
      icon: UserGroupIcon
    }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary-500/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <GlobeAltIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              NARDIHA <span className="text-primary-400">Ecosystem</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              50+ interconnected platforms sharing the NDH token economy, creating the most comprehensive blockchain ecosystem in the world.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="px-4 py-2 bg-dark-800 rounded-full text-gray-300">50+ Platforms</span>
              <span className="px-4 py-2 bg-dark-800 rounded-full text-gray-300">1M+ Users</span>
              <span className="px-4 py-2 bg-dark-800 rounded-full text-gray-300">$500M+ TVL</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ecosystem Map */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Our Platforms</h2>
              <p className="text-gray-400">A selection of platforms in the NARDIHA ecosystem</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {platforms.map((platform, idx) => {
                const Icon = platform.icon;
                return (
                  <motion.div
                    key={platform.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className={`relative bg-dark-800/50 rounded-xl p-6 border ${
                      platform.current ? 'border-primary-500' : 'border-dark-700'
                    } hover:border-primary-500/50 transition-colors`}
                  >
                    {platform.current && (
                      <span className="absolute -top-3 left-4 px-2 py-0.5 bg-primary-500 text-white text-xs rounded">
                        You are here
                      </span>
                    )}
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${platform.color} flex items-center justify-center mb-4`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xs text-gray-500 mb-1 block">Platform #{platform.id}</span>
                    <h3 className="text-lg font-semibold text-white mb-1">{platform.name}</h3>
                    <p className="text-gray-400 text-sm mb-3">{platform.description}</p>
                    <span className="px-2 py-1 bg-dark-700 text-gray-400 text-xs rounded">
                      {platform.category}
                    </span>
                  </motion.div>
                );
              })}
            </div>

            <div className="text-center mt-8">
              <p className="text-gray-500">
                + 42 more platforms in the ecosystem
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Ecosystem Benefits</h2>
              <p className="text-gray-400">Why the NARDIHA ecosystem is greater than the sum of its parts</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary-500/20 rounded-xl">
                        <Icon className="w-6 h-6 text-primary-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                        <p className="text-gray-400">{benefit.description}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* NDH Token */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-2xl p-8 md:p-12 border border-primary-500/30">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="text-primary-400 font-semibold mb-2 block">The Heart of the Ecosystem</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">NDH Token</h2>
                  <p className="text-gray-300 mb-6">
                    The NARDIHA Token (NDH) is the native utility and governance token that powers every platform in our ecosystem. Stake, vote, and earn across 50+ platforms with a single token.
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-gray-300">
                      <span className="w-2 h-2 bg-primary-400 rounded-full" />
                      Pay fees with discounts across all platforms
                    </li>
                    <li className="flex items-center gap-3 text-gray-300">
                      <span className="w-2 h-2 bg-primary-400 rounded-full" />
                      Stake for tiered benefits ecosystem-wide
                    </li>
                    <li className="flex items-center gap-3 text-gray-300">
                      <span className="w-2 h-2 bg-primary-400 rounded-full" />
                      Participate in unified DAO governance
                    </li>
                  </ul>
                  <Link
                    to="/ndh-token"
                    className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300"
                  >
                    Learn more about NDH
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
                <div className="flex justify-center">
                  <div className="w-48 h-48 bg-gradient-to-br from-primary-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-5xl font-bold text-white">NDH</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEMPLUM in Ecosystem */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <BuildingLibraryIcon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">
              TEMPLUM's Role
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              As Platform #20, TEMPLUM brings decentralized auctions to the NARDIHA ecosystem. NFTs minted on NFTVERSE can be auctioned here, tokens from DEXCHANGE can be used for bidding, and all transactions contribute to ecosystem-wide rewards.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <h3 className="text-white font-semibold mb-2">NFT Integration</h3>
                <p className="text-gray-400 text-sm">Auction NFTs from NFTVERSE and other platforms</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <h3 className="text-white font-semibold mb-2">DeFi Connection</h3>
                <p className="text-gray-400 text-sm">Use yields from YIELDMAX to fund bids</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <h3 className="text-white font-semibold mb-2">Cross-Chain</h3>
                <p className="text-gray-400 text-sm">Bridge assets via BRIDGE for auctions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Join the Ecosystem
            </h2>
            <p className="text-gray-400 mb-8">
              Start your journey in the NARDIHA ecosystem today
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/staking"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
              >
                Start Staking NDH
              </Link>
              <Link
                to="/auctions"
                className="px-6 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
              >
                Explore Auctions
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EcosystemPage;
